import type { ComponentPropsWithoutRef, ElementType, FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface ToneRootProps {
    initial: Tone
    children: ReactNode
    className?: string
}

// Holds the page's current tonal environment. Whichever marked section crosses
// the middle of the viewport sets the tone, and every colour token on the page
// glides to the new values.
export const ToneRoot: FC<ToneRootProps> = ({ initial, children, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [tone, setTone] = useState<Tone>(initial)

    useEffect(() => {
        const root = ref.current

        if (!root) return

        const active = new Map<Element, Tone>()

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                const value = (entry.target as HTMLElement).dataset.sectionTone as Tone | undefined

                if (entry.isIntersecting && value) active.set(entry.target, value)
                else active.delete(entry.target)
            }

            const latest = Array.from(active.values()).pop()

            if (latest) setTone(latest)
        }, { rootMargin: '-49% 0px -50% 0px', threshold: 0 })

        root.querySelectorAll('[data-section-tone]').forEach(element => observer.observe(element))

        return () => {
            observer.disconnect()
        }
    }, [])

    return (
        <div ref={ref} data-tone={tone} className={cn('tone-root grain relative min-h-screen', className)}>
            {children}
        </div>
    )
}

type ToneSectionProps<T extends ElementType> = {
    tone: Tone
    as?: T
    className?: string
    children?: ReactNode
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'className' | 'children'>

// A page section that declares which tonal environment it lives in.
export const ToneSection = <T extends ElementType = 'section'>({
    tone,
    as,
    className,
    children,
    ...rest
}: ToneSectionProps<T>): ReactNode => {
    const Tag: ElementType = as ?? 'section'

    return (
        <Tag data-section-tone={tone} className={cn('relative', className)} {...rest}>
            {children}
        </Tag>
    )
}
